import { useEffect, useState, useContext, useRef } from "react";
import PageWrapper from "../../components/PageWrapper";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import { useTranslation } from "react-i18next";
import UploadApplicationsDocumentsModal from "./UploadApplicationDocumentsModal";
// Assets
import StatusBadge from "../../components/StatusBadge";
import Loader from "../../components/Loader";
import InformationBox from "../../components/InformationBox";
import axiosClient from "../../services/axiosClient";
import { UserAuth } from "../../utils/AuthContext";
import ErrorBox from "../../components/ErrorBox";
import useLanguage from "../../utils/useLanguage";
import Tooltip from "../../components/Tooltip";
import arrowIcon from "../../assets/arrow-purple.png";
import BanksInvoicesApplicationsFilters from "./BanksInvoicesApplicationsFilters";
import { formatDate, toDate } from "../../utils/toDate";

function calculatePercentage(part, base) {
  // Calculate total
  const total = part + base;

  // Handle edge case where total is 0
  if (total === 0) {
    return 0;
  }

  // Calculate percentage
  const percentage = (part / total) * 100;

  // Round to 2 decimals
  return Math.round(percentage * 100) / 100;
}

const getPaymentStatusBadge = (
  latestPayment,
  userRole,
  userId,
  totalPaid,
  remainingAmount,
  invoiceId,
  invoiceHasCreatorOrAuditor,
  generalStatus,
  bankExecutor,
  t,
  currency,
) => {
  // If no payment exists
  if (!latestPayment) {
    return <StatusBadge color={"gray"} title={"noNewPayments"} />;
  }

  if (generalStatus === "SENT_TO_POLICIES") {
    return <StatusBadge color={"blue"} title={"sentToPolicy"} />;
  }

  // Check if payment is fully paid
  const isFullyPaid = totalPaid >= remainingAmount + totalPaid;
  if (isFullyPaid && !latestPayment?.transactionNumber) {
    return (
      <Tooltip
        maxWidth={400}
        content={
          (
            <div className="o-column o-column--vertical-align_start u-width_full-width">
              <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                <span className="u-width--min-width_100 t-text-align_start">
                  {t("executer")}:
                </span>
                <span className="t-bold_bold">{bankExecutor?.name}</span>
              </div>
              <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                <span className="u-width--min-width_100 t-text-align_start">
                  {t("lastPaymentAmount")}:
                </span>
                <span className="t-bold_bold">
                  {`${currency || ""} ${latestPayment?.amount}` || "-"}
                </span>
              </div>
              <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                <span className="u-width--min-width_100 t-text-align_start">
                  {t("executionDate")}:
                </span>
                <span className="t-bold_bold">
                  {formatDate(bankExecutor?.date)}
                </span>
              </div>
            </div>
          ) || "-"
        }
      >
        <StatusBadge color={"green"} title={"completed"} />
      </Tooltip>
    );
  }

  // Check if creator, auditor, or executor rejected the payment
  if (
    latestPayment.creatorStatus === "REJECTED" ||
    latestPayment.auditorStatus === "REJECTED" ||
    latestPayment.executorStatus === "REJECTED"
  ) {
    return <StatusBadge color={"red"} title={"declined"} />;
  }

  // Determine which status to show based on user role
  if (userRole === "applicationsCreator") {
    // Check if creator hasn't started and creatorId is null
    if (
      latestPayment.creatorStatus === "NOT_STARTED" &&
      latestPayment.creatorId === null
    ) {
      return (
        <a
          href={
            invoiceHasCreatorOrAuditor
              ? `/banks-invoices-applications/${invoiceId}/payments/${latestPayment.id}`
              : null
          }
          disabled={!invoiceHasCreatorOrAuditor}
          style={{ textDecoration: "none", cursor: "pointer" }}
        >
          <StatusBadge color={"orange"} title={"unstartedPayment"} />
        </a>
      );
    }
    // Show creator's status
    switch (latestPayment.creatorStatus) {
      case "NOT_STARTED":
        return <StatusBadge color={"orange"} title={"unstartedPayment"} />;
      case "PENDING":
        return <StatusBadge color={"blue"} title={"inProgress"} />;

      case "ACCEPTED":
        return (
          <Tooltip
            maxWidth={400}
            content={
              (
                <div className="o-column o-column--vertical-align_start u-width_full-width">
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("executer")}:
                    </span>
                    <span className="t-bold_bold">{bankExecutor?.name}</span>
                  </div>
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("lastPaymentAmount")}:
                    </span>
                    <span className="t-bold_bold">
                      {currency || ""} {`${latestPayment?.amount}` || "-"}
                    </span>
                  </div>
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("executionDate")}:
                    </span>
                    <span className="t-bold_bold">
                      {formatDate(bankExecutor?.date)}
                    </span>
                  </div>
                </div>
              ) || "-"
            }
          >
            <StatusBadge
              color={latestPayment?.transactionNumber ? "royal-green" : "gray"}
              title={latestPayment?.transactionNumber || "noNewPayments"}
            />
          </Tooltip>
        );

      default:
        return <StatusBadge color={"orange"} title={"unstartedPayment"} />;
    }
  } else if (userRole === "applicationsAuditor") {
    // Check if auditor hasn't started and auditorId is null
    if (
      latestPayment.auditorStatus === "NOT_STARTED" &&
      latestPayment.auditorId === null
    ) {
      return (
        <a
          href={
            invoiceHasCreatorOrAuditor
              ? `/banks-invoices-applications/${invoiceId}/payments/${latestPayment.id}`
              : null
          }
          disabled={!invoiceHasCreatorOrAuditor}
          style={{ textDecoration: "none", cursor: "pointer" }}
        >
          <StatusBadge color={"orange"} title={"unstartedPayment"} />
        </a>
      );
    }
    // Show auditor's status
    switch (latestPayment.auditorStatus) {
      case "NOT_STARTED":
        return (
          <div>
            <StatusBadge color={"orange"} title={"unstartedPayment"} />
          </div>
        );
      case "PENDING":
        return <StatusBadge color={"blue"} title={"inProgress"} />;

      case "ACCEPTED":
        return (
          <Tooltip
            maxWidth={400}
            content={
              (
                <div className="o-column o-column--vertical-align_start u-width_full-width">
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("executer")}:
                    </span>
                    <span className="t-bold_bold">{bankExecutor?.name}</span>
                  </div>
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("lastPaymentAmount")}:
                    </span>
                    <span className="t-bold_bold">
                      {`${latestPayment?.amount}` || "-"} {currency || ""}
                    </span>
                  </div>
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("executionDate")}:
                    </span>
                    <span className="t-bold_bold">
                      {formatDate(bankExecutor?.date)}
                    </span>
                  </div>
                </div>
              ) || "-"
            }
          >
            <StatusBadge
              color={latestPayment?.transactionNumber ? "royal-green" : "gray"}
              title={latestPayment?.transactionNumber || "noNewPayments"}
            />
          </Tooltip>
        );

      default:
        return <StatusBadge color={"orange"} title={"unstartedPayment"} />;
    }
  } else if (userRole === "paymentExecutor") {
    // Check if executor hasn't started and executorId is null
    if (
      latestPayment.executorStatus === "NOT_EXECUTED" &&
      latestPayment.executorId === null
    ) {
      return (
        <a
          href={
            invoiceHasCreatorOrAuditor
              ? `/banks-invoices-applications/${invoiceId}/payments/${latestPayment.id}`
              : null
          }
          disabled={!invoiceHasCreatorOrAuditor}
          style={{ textDecoration: "none", cursor: "pointer" }}
        >
          <StatusBadge
            color={invoiceHasCreatorOrAuditor ? "orange" : "orange-striped"}
            title={"unstartedPayment"}
          />
        </a>
      );
    }
    // Show executor's status
    switch (latestPayment.executorStatus) {
      case "NOT_STARTED":
        return <StatusBadge color={"orange"} title={"unstartedPayment"} />;
      case "NOT_EXECUTED":
        return <StatusBadge color={"orange"} title={"unstartedPayment"} />;
      case "PENDING":
        return <StatusBadge color={"blue"} title={"inProgress"} />;
      case "EXECUTED":
        return (
          <Tooltip
            maxWidth={500}
            content={
              (
                <div className="o-column o-column--vertical-align_start u-width_full-width">
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("executer")}:
                    </span>
                    <span className="t-bold_bold">{bankExecutor?.name}</span>
                  </div>
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("lastPaymentAmount")}:
                    </span>
                    <span className="t-bold_bold">
                      {`${currency || ""} ${latestPayment?.amount}` || "-"}
                    </span>
                  </div>
                  <div className="u-width_full-width o-row o-row--horizontal-align_space-between">
                    <span className="u-width--min-width_100 t-text-align_start">
                      {t("executionDate")}:
                    </span>
                    <span className="t-bold_bold">
                      {formatDate(bankExecutor?.date)}
                    </span>
                  </div>
                </div>
              ) || "-"
            }
          >
            <StatusBadge
              color={latestPayment?.transactionNumber ? "royal-green" : "gray"}
              title={latestPayment?.transactionNumber || "noNewPayments"}
            />
          </Tooltip>
        );
      default:
        return <StatusBadge color={"orange"} title={"unstartedPayment"} />;
    }
  }

  // Default fallback
  return <StatusBadge color={"gray"} title={"unstartedPayment"} />;
};

const BanksInvoicesApplications = () => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const [data, setData] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 20,
    totalRecords: 0,
  });
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [selectedSender, setSelectedSender] = useState(null);
  const [myTransactions, setMyTransactions] = useState(null);

  const [searchParams, setSearchParams] = useState({});

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "invoiceNumber",
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState({
    id: "",
    nameInAr: "selectAll",
  });
  const [loading, setLoading] = useState(false);
  const [gettingInvoicesError, setGettingInvoicesError] = useState("");

  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [includeRejected, setIncludeRejected] = useState(false);
  const [sortPayment, setSortPayment] = useState("asc");
  const [includeAppsNotPassedByMaker, setIncludeAppsNotPassedByMaker] =
    useState(false);

  const abortControllerRef = useRef(null);

  const tableTitles = [
    {
      value: "invoiceNumber",
    },
    {
      value: "companyName",
    },
    {
      value: "lastPaymentDate",
      content: (
        <div
          onClick={() => setSortPayment(sortPayment === "asc" ? "desc" : "asc")}
          style={{ cursor: "pointer" }}
          className="o-row o-row--horizontal-align_center o-row--vertical-align_center"
        >
          <span className="bold-text cell-title">{t("lastPaymentDate")}</span>
          <img
            src={arrowIcon}
            alt="sort"
            width={15}
            height={15}
            style={{
              // This logic flips the icon 180 degrees when state is "desc"
              transform:
                sortPayment === "desc" ? "rotate(180deg)" : "rotate(0deg)",
              // Optional: adds a smooth animated transition
              transition: "transform 0.2s ease-in-out",
              marginLeft: "8px",
            }}
          />
        </div>
      ),
      onClick: () => setSortPayment(sortPayment === "asc" ? "desc" : "asc"),
      keepOnMobile: true,
    },
    {
      value: "payments",
      keepOnMobile: true,
    },

    {
      value: "creator",
      keepOnMobile: true,
    },
    {
      value: "status",
      keepOnMobile: true,
    },
    {
      value: "auditor",
      keepOnMobile: true,
    },
    {
      value: "status",
      keepOnMobile: true,
    },
    { title: "paid", value: "paid" },
  ];

  const searchTitles = [
    {
      title: t("invoiceNumber"),
      value: "invoiceNumber",
    },
    {
      title: t("invoiceDate"),
      value: "date",
    },
    {
      title: t("paymentsDate"),
      value: "paymentsDate",
    },
    {
      title: t("invoiceStatus"),
      value: "status",
    },
    {
      title: t("paymentsStatus"),
      value: "paymentsStatus",
    },
  ];

  const statusOptions = [
    { id: "", nameInAr: t("selectAll") },
    { id: "NOT_STARTED", nameInAr: t("notStarted") },
    { id: "PENDING", nameInAr: t("inProgress") },
    { id: "ACCEPTED", nameInAr: t("approved") },
    { id: "REJECTED", nameInAr: t("declined") },
  ];

  const payemntsStatus = [
    { id: "", nameInAr: t("selectAll") },
    { id: "executed", nameInAr: t("paymentExecuted") },
    { id: "unstartedPayments", nameInAr: t("unstartedPayments") },
    { id: "rejected", nameInAr: t("declined") },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const handleSearchType = (searchType) => {
    setSelectedTitle(searchType);
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setSelectedStatus({ id: "", nameInAr: t("selectAll") });
    setSearchParams({});
  };

  // format new date to  dd/mm/yyyy
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSearch = (queryOverride) => {
    const isOverride = typeof queryOverride === "string";

    const params = {};
    if (
      selectedTitle.value === "date" ||
      selectedTitle.value === "paymentsDate"
    ) {
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
    } else if (selectedTitle.value === "status") {
      if (selectedStatus.id) params.status = selectedStatus.id;
    } else if (selectedTitle.value === "paymentsStatus") {
      // Add this condition
      if (selectedStatus.id) params.paymentsStatus = selectedStatus.id;
    } else {
      const query = isOverride ? queryOverride : searchQuery;
      if (query) {
        params[selectedTitle.value] = query;
      }
    }

    // Store the current search filters, so when moving between pages we keep the search value
    setSearchParams(params);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // getAllAnalyzedApplications(params);
  };

  const getAllAnalyzedApplications = async (params = {}) => {
    // Abort the previous request if it's still in flight
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const searchParams = {
      type: selectedTitle.value,
      query: params[selectedTitle?.value] || undefined,
      startDate: params.startDate || undefined,
      endDate: params.endDate || undefined,
      status: params.status || undefined,
      paymentsStatus: params.paymentsStatus || undefined, // Add this line
      page: params.page || pagination.currentPage,
      limit: params.limit || pagination.limit,
      supplierId: selectedReceiver ? selectedReceiver.id : undefined,
      subCompanyId: selectedSender ? selectedSender.id : undefined,
      userRole: userData.role,
      includeCompleted: includeCompleted,
      includeRejected: includeRejected,
      includeAppsNotPassedByMaker: includeAppsNotPassedByMaker,
      sortPayment: sortPayment,
    };

    try {
      setGettingInvoicesError(null);
      setLoading(true);
      const response = await axiosClient.get(
        "/bankAndSubcompany/getBankMultiContainersInvoices",
        {
          params: searchParams,
          signal: controller.signal,
          headers: {
            "user-id":
              myTransactions != null && myTransactions != "all"
                ? userData.userId
                : null, // If myTransactions is true, use the userId to filter the invoices
          },
        },
      );

      if (response.status === 200) {
        const userRole = localStorage.getItem("role");
        const formatedData = response.data.invoices.map((item) => {
          const paidPercentage = Math.round(
            calculatePercentage(item?.totalPaid, item?.remainingAmount),
            4,
          );

          return [
            {
              type: TABLE_CELL_TYPES.LINK,
              value: item.invoiceNumber || "-",
              to: `/banks-invoices-applications/${item.id}`,
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value: (
                <Tooltip
                  content={
                    currentDirection === "rtl"
                      ? item.subCompany.nameInAr || "-"
                      : item.subCompany.name || "-"
                  }
                >
                  {currentDirection === "rtl"
                    ? item.subCompany.nameInAr
                        .split(" ")
                        .slice(0, 4)
                        .join(" ") || "-"
                    : item.subCompany.name.split(" ").slice(0, 4).join(" ") ||
                      "-"}
                  ...
                </Tooltip>
              ),
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.TEXT,
              value: formatDate(item.latestPaymentDetail?.date) || "-",
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value: getPaymentStatusBadge(
                item.latestPaymentDetail,
                userData.role,
                userData.userId,
                item.totalPaid,
                item.remainingAmount,
                item.id,
                Boolean(
                  (userRole === "applicationsCreator" &&
                    item.bankCreator?.id) ||
                  (userRole === "applicationsAuditor" &&
                    item.bankAuditor?.id) ||
                  (userRole === "paymentExecutor" &&
                    item.bankAuditor?.id &&
                    item.bankAuditor?.status === "ACCEPTED"),
                ),
                item?.latestPaymentDetail?.status,
                item.bankExecutor,
                t,
                item.currency,
              ),
              keepOnMobile: true,
            },

            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value: (
                <div>
                  <div>{item?.bankCreator?.name || t("none")}</div>
                  {item?.creationDate && (
                    <div
                      className={"t-text-mini u-margin--top_100 t-bold_bold "}
                    >
                      {formatDateToDDMMYYYY(item?.creationDate)}
                    </div>
                  )}
                </div>
              ),
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value:
                item.bankCreator.status === "NOT_STARTED" ? (
                  <StatusBadge color={"gray"} title={"notStarted"} />
                ) : item.bankCreator.status === "PENDING" ? (
                  <StatusBadge color={"blue"} title={"inProgress"} />
                ) : item.bankCreator.status === "REJECTED" ? (
                  <StatusBadge color={"red"} title={"declined"} />
                ) : item.bankCreator.status === "ACCEPTED" ? (
                  <StatusBadge color={"green"} title={"approved"} />
                ) : null,
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value: (
                <div>
                  <div>{item?.bankAuditor?.name || t("none")}</div>
                  {item?.auditionDate && (
                    <div
                      className={"t-text-mini u-margin--top_100 t-bold_bold "}
                    >
                      {formatDateToDDMMYYYY(item?.auditionDate)}
                    </div>
                  )}
                </div>
              ),
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value:
                item.bankAuditor.status === "NOT_STARTED" ? (
                  <StatusBadge color={"gray"} title={"notStarted"} />
                ) : item.bankAuditor.status === "PENDING" ? (
                  <StatusBadge color={"blue"} title={"inProgress"} />
                ) : item.bankAuditor.status === "REJECTED" ? (
                  <StatusBadge color={"red"} title={"declined"} />
                ) : item.bankAuditor.status === "ACCEPTED" ? (
                  <StatusBadge color={"green"} title={"approved"} />
                ) : null,
              keepOnMobile: true,
            },
            {
              type: TABLE_CELL_TYPES.ELEMENT,
              value: (
                <p
                  className="t-bold_bold"
                  style={{ color: paidPercentage >= 100 ? "green" : "" }}
                >
                  {paidPercentage}%
                </p>
              ),
              keepOnMobile: true,
            },
          ];
        });

        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalInvoices,
          currentPage: response.data.currentPage,
        }));
        setData(formatedData);
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      console.error(error);
      setGettingInvoicesError("errorGettingInvoices");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getAllAnalyzedApplications({
      ...searchParams,
      page: pagination.currentPage,
    });
  }, [
    pagination.currentPage,
    selectedReceiver,
    selectedSender,
    myTransactions,
    includeCompleted,
    sortPayment,
    searchParams,
    includeRejected,
    includeAppsNotPassedByMaker,
  ]);

  return (
    <PageWrapper title={t("transferOrders")}>
      <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400">
        <span>
          {userData?.userType === "organization"
            ? `${t("organization")}:`
            : `${t("user")}:`}
        </span>
        <span>{`${localStorage.getItem("username")}`}</span>
      </div>

      {/* Filters Component */}
      <BanksInvoicesApplicationsFilters
        t={t}
        loading={loading}
        selectedReceiver={selectedReceiver}
        setSelectedReceiver={setSelectedReceiver}
        selectedSender={selectedSender}
        setSelectedSender={setSelectedSender}
        myTransactions={myTransactions}
        setMyTransactions={setMyTransactions}
        searchTitles={searchTitles}
        selectedTitle={selectedTitle}
        handleSearchType={handleSearchType}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        statusOptions={statusOptions}
        payemntsStatus={payemntsStatus}
        handleSearch={handleSearch}
        formatDate={formatDate}
        includeCompleted={includeCompleted}
        setIncludeCompleted={setIncludeCompleted}
        includeRejected={includeRejected}
        setIncludeRejected={setIncludeRejected}
        includeAppsNotPassedByMaker={includeAppsNotPassedByMaker}
        setIncludeAppsNotPassedByMaker={setIncludeAppsNotPassedByMaker}
        setPagination={setPagination}
      />
      {gettingInvoicesError && <ErrorBox>{t(gettingInvoicesError)}</ErrorBox>}
      {loading && <Loader />}

      {!loading && !gettingInvoicesError && data.length > 0 && (
        <RoundedTable
          titles={tableTitles}
          rows={data}
          pagination={pagination}
          setPagination={setPagination}
          mobileEffect={true}
        />
      )}
      {!loading && !gettingInvoicesError && data.length === 0 && (
        <InformationBox>
          <p>{t("noApplicationsFound")}</p>
        </InformationBox>
      )}
      {showUploadModal && (
        <UploadApplicationsDocumentsModal
          onClose={() => setShowUploadModal(false)}
        />
      )}
    </PageWrapper>
  );
};

export default BanksInvoicesApplications;
